import style from './LeadGenFormModule.scss';

import { Asset, ReferencedEntry } from 'contentful';
import * as React from 'react';
import { ContentfulImage } from 'src/components/Contentful/ContentfulImage';

import { FormField, LeadGenFormAssembly, TextAlignment } from 'src/api/contentful/contentTypes.cloverdotcom';
import { getField, isEntry } from 'src/utils/EntryUtil';

import { applyEntryDataAttributes } from '../EntryWrapper/EntryDataWrapper';

import { TextStack } from '../TextStack/TextStack';

import cx from 'classnames';
import { ContactForm } from 'src/components/Contact/ContactForm';
import { findFirstFieldOfType } from '../Contact/ContactFormBody';
import { ApiClientMapper, FormEndpointMapper, FormEndpointType } from '../Contact/FormEndpoints';
import { SomethingWentWrongPage } from '../ErrorPage/SomethingWentWrongPage';
import { useResponsive } from '../Responsive/Responsive';

interface LeadGenFormModuleProps extends React.AllHTMLAttributes<HTMLElement>{
  entry?: ReferencedEntry<LeadGenFormAssembly>;
  submitted?: boolean;
  error?: boolean;
}

export const LeadGenFormModuleWrapper: React.FC<{
  image: Asset | undefined;
  textWrapperClassName: string;
  textAlignment: TextAlignment | undefined;
  className: string | undefined;
  otherProps: object | undefined;
  showImage: boolean;
}> = ({ image, textWrapperClassName, children, textAlignment, className, showImage, otherProps }) => {
  return (
    <section className={cx(style.leadGenFormModuleWrapper, className)} {...otherProps}>
      <div className={style.leadGenFormModule}>
        {showImage && (
          <div
            className={cx(style.imageWrapper,
              textAlignment === TextAlignment.RIGHT ? style.imageLeftAligned : style.imageRightAligned)}
          >
            <ContentfulImage
              asset={image}
              className={style.moduleImage}
            />
          </div>
        )}
        <div
          className={cx(textWrapperClassName,
            textAlignment === TextAlignment.RIGHT ? style.textRightAligned : style.textLeftAligned)}
        >
          {children}
        </div>
      </div>
    </section>
  );
};

export const formFieldsEntryToArray = (fields: ReadonlyArray<ReferencedEntry<FormField>>) => {
  return fields.reduce((acc: FormField[], entry) => {
    const currentFieldType = getField(entry, 'fieldType');
    if (!currentFieldType) return acc;

    const options = getField(entry, 'options');
    const displayDetail = getField(entry, 'displayDetail');
    const predefinedField = getField(entry, 'predefinedField');

    const currentField = findFirstFieldOfType(acc, currentFieldType);
    if (!currentField) {
      const newFormField = {
        fieldType: currentFieldType,
        options,
        displayDetail,
        predefinedField,
      };

      if (!displayDetail) { delete newFormField.displayDetail; }
      if (!options) { delete newFormField.options; }
      if (!predefinedField) { delete newFormField.predefinedField; }

      acc.push(newFormField);
    }

    return acc;
  }, []);
};

const LeadGenFormModule: React.FC<LeadGenFormModuleProps>
  = ({ entry, className, submitted = false, error = false, ...otherProps }) => {
  const [ isFormSubmitted, setIsFormSubmitted ] = React.useState(submitted);
  const [ isFormError, setIsFormError ] = React.useState(error);

  const { browser } = useResponsive();

  const topic = getField(entry, 'topic');
  const postSubmissionTopic = getField(entry, 'postSubmissionTopic');
  const textAlignment = getField(entry, 'textAlignment');
  const formFields = formFieldsEntryToArray(getField(entry, 'formFields', []));

  const formImage = getField(topic, 'image');
  const postSubmissionImage = getField(postSubmissionTopic, 'image');

  const endpointField = getField(entry, 'endpoint') || FormEndpointType.LEADS;

  const endpointFn = FormEndpointMapper[endpointField];
  const apiClient = ApiClientMapper[endpointField];

  const isDesktop = browser.is.desktop;

  if (!isEntry(topic)) return null;

  if (isFormError) {
    return (
      <LeadGenFormModuleWrapper
        image={formImage}
        textWrapperClassName={style.textWrapper}
        textAlignment={textAlignment}
        className={className}
        otherProps={otherProps}
        showImage={isDesktop}
      >
        <SomethingWentWrongPage />
      </LeadGenFormModuleWrapper>
    );
  }

  if (isFormSubmitted) {
    return (
      <LeadGenFormModuleWrapper
        image={postSubmissionImage}
        textWrapperClassName={style.textWrapperPostSubmission}
        textAlignment={textAlignment}
        className={className}
        otherProps={otherProps}
        showImage={isDesktop}
      >
        <TextStack
          entry={postSubmissionTopic}
          titleTagName="h2"
          linkClassName={style.optionalTextButton}
          titleClassName={style.textTitle}
        />
      </LeadGenFormModuleWrapper>
    );
  }

  const isBrowserMobileOrTablet = browser.is.mobile || browser.is.tablet;

  const textStack = (
    <TextStack
      entry={topic}
      titleTagName="h2"
      titleClassName={style.textTitle}
      blurbClassName={isBrowserMobileOrTablet ? style.textBlurb : undefined}
    />
  );

  const contactForm = (
    <ContactForm
      apiClient={apiClient}
      onSuccess={() => setIsFormSubmitted(true)}
      onError={() => setIsFormError(true)}
      formFields={formFields}
      endpoint={endpointFn}
    />
  );

  if (isBrowserMobileOrTablet) {
    return (
      <section className={cx(style.leadGenFormModuleWrapper, className)} {...otherProps}>
        <div className={style.leadGenFormModule}>
          {textStack}
          {contactForm}
        </div>
      </section>
    );
  }

  return (
    <LeadGenFormModuleWrapper
      image={formImage}
      textWrapperClassName={style.textWrapper}
      textAlignment={textAlignment}
      className={className}
      otherProps={otherProps}
      showImage={isDesktop}
    >
      {textStack}
      {contactForm}
    </LeadGenFormModuleWrapper>
  );
};

const Wrapped = applyEntryDataAttributes(LeadGenFormModule);

export { Wrapped as LeadGenFormModule };
